import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getStoredClientId } from "../utils/tokenUtils";
import { migrationService } from "../services/migrationService";
import { projectService } from "../services/projectService";
import { Button } from "../components/ui/button";
import { Square, Database, FileText } from "lucide-react";
import TableSkeleton from "../components/skeletons/TableSkeleton";
import { InlineLoadSkeleton } from "../components/common/InlineLoadSkeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import toast from "react-hot-toast";

const POLL_INTERVAL_MS = 5000;
const MIGRATION_TERMINAL = ["completed", "failed", "cancelled"];
const MIGRATION_CANCELLABLE = ["queued", "processing"];

/** Each selected project runs as a separate migration job so you can track and retry per project. */

function formatDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  return dt.toLocaleString();
}

export default function MigrationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const clientId =
    searchParams.get("client_id") || getStoredClientId() || user?.client_id;
  const [projects, setProjects] = useState([]);

  const [migrations, setMigrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cancelingIds, setCancelingIds] = useState(() => new Set());
  const [logsModalId, setLogsModalId] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsMigration, setLogsMigration] = useState(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [failureReportId, setFailureReportId] = useState(null);
  const [failureReport, setFailureReport] = useState(null);
  const [failureReportLoading, setFailureReportLoading] = useState(false);
  const pollIds = useRef(new Set());

  useEffect(() => {
    if (!clientId) return;
    projectService
      .getProjects({ client_id: clientId, limit: 500, is_active: true })
      .then((data) => {
        const list = data?.projects || data?.data || [];
        setProjects(Array.isArray(list) ? list : []);
      })
      .catch(() => setProjects([]));
  }, [clientId]);

  const loadMigrations = useCallback(
    async (silent = false) => {
      if (!clientId) return;
      if (!silent) setLoading(true);
      try {
        const res = await migrationService.getHistory({
          client_id: clientId,
          limit: 50,
        });
        const list = res.migrations || [];
        setMigrations(list);
        list.forEach((m) => {
          if (m.id && m.status && !MIGRATION_TERMINAL.includes(m.status)) {
            pollIds.current.add(m.id);
          }
        });
      } catch (e) {
        if (!silent)
          toast.error(e.response?.data?.error || "Failed to load migrations");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [clientId],
  );

  const pollStatus = useCallback(async () => {
    if (!clientId || document.hidden) return;
    for (const mid of Array.from(pollIds.current)) {
      try {
        const res = await migrationService.getStatus(mid);
        const m = res.migration;
        if (m && m.status && MIGRATION_TERMINAL.includes(m.status)) {
          pollIds.current.delete(mid);
        }
      } catch (_) {}
    }
    loadMigrations(true);
  }, [clientId, loadMigrations]);

  useEffect(() => {
    if (!clientId) return;
    loadMigrations();
  }, [clientId, loadMigrations]);

  useEffect(() => {
    if (!clientId) return;
    let timerId;
    const schedule = () => {
      if (document.hidden) return;
      timerId = setTimeout(() => {
        pollStatus();
        schedule();
      }, POLL_INTERVAL_MS);
    };
    schedule();
    const onVisibility = () => {
      if (document.hidden && timerId) {
        clearTimeout(timerId);
        timerId = null;
      } else if (!document.hidden && !timerId) {
        schedule();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      if (timerId) clearTimeout(timerId);
    };
  }, [clientId, pollStatus]);

  const handleCancel = async (id) => {
    if (!id) return;
    setCancelingIds((prev) => new Set(prev).add(id));
    try {
      const res = await migrationService.cancel(id);
      toast.success("Cancel requested");
      if (res.migration) {
        setMigrations((prev) =>
          prev.map((m) =>
            m.id === id
              ? { ...m, cancel_requested: true, ...res.migration }
              : m,
          ),
        );
      }
      loadMigrations();
    } catch (e) {
      toast.error(
        e.response?.data?.error || e.message || "Failed to cancel migration",
      );
    } finally {
      setCancelingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleViewLogs = async (id) => {
    setLogsModalId(id);
    setLogsLoading(true);
    setLogsMigration(null);
    try {
      const [logsRes, statusRes] = await Promise.all([
        migrationService.getLogs(id, { limit: 200 }),
        migrationService.getStatus(id),
      ]);
      setLogs(logsRes.logs || []);
      setLogsMigration(statusRes.migration || null);
    } catch (_) {
      setLogs([]);
      setLogsMigration(null);
    } finally {
      setLogsLoading(false);
    }
  };

  const handleViewFailureReport = async (id) => {
    setFailureReportId(id);
    setFailureReportLoading(true);
    setFailureReport(null);
    try {
      const res = await migrationService.getFailureReport(id, { limit: 200 });
      setFailureReport(res.failure_report || null);
    } catch (_) {
      setFailureReport(null);
    } finally {
      setFailureReportLoading(false);
    }
  };

  if (!clientId) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">
          Migration is available for your client. Open the Projects page and use
          Migration from the sidebar, or ensure you are assigned to a client.
        </p>
      </div>
    );
  }

  const projectNameById = (id) =>
    projects.find((p) => p.project_id === id)?.name || id?.slice(0, 8) || "—";

  return (
    <div className="p-6 max-w-6xl">
      <h1 className="text-2xl font-bold mb-6">Migration</h1>
      <p className="text-sm text-muted-foreground mb-4">
        Migrate test cases from TestRail, Tuskr, BrowserStack, or CSV/Excel.
        Connect your source, choose what to import, then start the migration.
        When you select multiple projects, one migration job is created per
        project so you can track and retry each one separately.
      </p>
      <div className="flex items-center gap-4 mb-4 flex-wrap">
        <Button
          onClick={() => navigate(`/migration/wizard?client_id=${clientId}`)}
        >
          <Database className="h-4 w-4 mr-2" />
          Start Migration
        </Button>
      </div>
      <div className="border rounded-md overflow-auto">
        {loading ? (
          <div className="p-4">
            <TableSkeleton rows={6} columns={8} />
          </div>
        ) : migrations.length > 0 ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="text-left p-2">Migration ID</th>
                <th className="text-left p-2">Source</th>
                <th className="text-left p-2">Project</th>
                <th className="text-left p-2">Scope</th>
                <th className="text-left p-2">Records</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Created</th>
                <th className="text-left p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {migrations.map((m) => (
                <tr key={m.id} className="border-b">
                  <td className="p-2 font-mono text-xs">
                    {m.id?.slice(0, 8)}…
                  </td>
                  <td className="p-2">{m.source || "—"}</td>
                  <td className="p-2">{projectNameById(m.project_id)}</td>
                  <td className="p-2">{m.scope || "—"}</td>
                  <td className="p-2">
                    {m.processed_records != null
                      ? `${m.processed_records}/${m.total_records || 0}`
                      : (m.total_records ?? "—")}
                  </td>
                  <td className="p-2">
                    {m.cancel_requested &&
                    (m.status === "queued" || m.status === "processing")
                      ? "Cancelling…"
                      : m.status}
                  </td>
                  <td className="p-2">{formatDate(m.created_at)}</td>
                  <td className="p-2 flex gap-1 flex-wrap">
                    {MIGRATION_CANCELLABLE.includes(m.status) &&
                      !m.cancel_requested && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-destructive border-destructive/50 hover:bg-destructive/10"
                          onClick={() => handleCancel(m.id)}
                          disabled={cancelingIds.has(m.id)}
                        >
                          {cancelingIds.has(m.id) ? (
                            <InlineLoadSkeleton className="h-3.5 w-3.5 rounded" />
                          ) : (
                            <Square className="h-3.5 w-3.5" />
                          )}
                          Cancel
                        </Button>
                      )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewLogs(m.id)}
                    >
                      <FileText className="h-3.5 w-3.5 mr-1" />
                      Logs
                    </Button>
                    {(m.status === "failed" ||
                      (m.failed_records != null && m.failed_records > 0)) && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-destructive/80 border-destructive/30 hover:bg-destructive/10"
                        onClick={() => handleViewFailureReport(m.id)}
                      >
                        Failure report
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-6 text-center text-muted-foreground">
            No migration history yet. Start a migration to import from TestRail,
            Tuskr, BrowserStack, or CSV/Excel.
          </div>
        )}
      </div>

      <Dialog
        open={!!logsModalId}
        onOpenChange={(open) =>
          !open && (setLogsModalId(null), setLogsMigration(null))
        }
      >
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              Migration logs{" "}
              {logsModalId ? `(${logsModalId.slice(0, 8)}…)` : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-auto flex-1 text-sm space-y-4">
            {logsLoading ? (
              <div className="p-2">
                <TableSkeleton rows={5} columns={4} />
              </div>
            ) : (
              <>
                {logsMigration?.status === "failed" &&
                  logsMigration?.error_message && (
                    <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20">
                      <p className="font-medium text-destructive">
                        Failure reason
                      </p>
                      <p className="mt-1 break-words">
                        {logsMigration.error_message}
                      </p>
                    </div>
                  )}
                {logs.length > 0 ? (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2">Type</th>
                        <th className="text-left p-2">Record ID</th>
                        <th className="text-left p-2">Status</th>
                        <th className="text-left p-2">Message</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log, i) => (
                        <tr key={log.id || i} className="border-b">
                          <td className="p-2">{log.record_type}</td>
                          <td className="p-2 font-mono text-xs">
                            {log.record_id ?? "—"}
                          </td>
                          <td className="p-2">{log.status}</td>
                          <td className="p-2">{log.message ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  !logsMigration?.error_message && (
                    <div className="p-4 text-muted-foreground">No logs.</div>
                  )
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!failureReportId}
        onOpenChange={(open) => !open && setFailureReportId(null)}
      >
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              Failure report{" "}
              {failureReportId ? `(${failureReportId.slice(0, 8)}…)` : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-auto flex-1 text-sm space-y-4">
            {failureReportLoading ? (
              <div className="p-2">
                <TableSkeleton rows={5} columns={4} />
              </div>
            ) : failureReport ? (
              <>
                {failureReport.error_message && (
                  <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20">
                    <p className="font-medium text-destructive">
                      Job failure reason
                    </p>
                    <p className="mt-1 break-words">
                      {failureReport.error_message}
                    </p>
                  </div>
                )}
                <p className="text-muted-foreground">
                  Failed records: {failureReport.failed_records_count ?? 0}
                </p>
                {failureReport.failures && failureReport.failures.length > 0 ? (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2">Type</th>
                        <th className="text-left p-2">Record ID</th>
                        <th className="text-left p-2">Message</th>
                      </tr>
                    </thead>
                    <tbody>
                      {failureReport.failures.map((row, i) => (
                        <tr key={row.id || i} className="border-b">
                          <td className="p-2">{row.record_type}</td>
                          <td className="p-2 font-mono text-xs">
                            {row.record_id ?? "—"}
                          </td>
                          <td className="p-2 break-words">
                            {row.message ?? "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  !failureReport.error_message && (
                    <div className="p-4 text-muted-foreground">
                      No failed records in logs.
                    </div>
                  )
                )}
              </>
            ) : (
              <div className="p-4 text-muted-foreground">
                No failure report.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
